import React, {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Layout} from "../../pages/siren/Layout";
import {BlueButton} from "../../components/ui/Buttons";
import {SirenDefaultBroadcastConfig} from "./hooks/useGetSirenDefaultBroadcastConfigs";
import {useUpsertSirenDefaultBroadcastConfig} from "./hooks/useUpsertSirenDefaultBroadcastConfig";
import {useDeleteSirenDefaultBroadcastConfig} from "./hooks/useDeleteSirenDefaultBroadcastConfig";
import {platformAPI} from "../../api/axiosConfig";
import {ClientSelectSingle} from "../../components/form-v2/searchable-select/ClientSelect";
import {DepartmentSelectSingle} from "../../components/form-v2/searchable-select/DepartmentSelect";
import {IntervalInputV2, IntervalFormat} from "../../pages/siren/components/IntervalInputV2";
import {Client, Department} from "../../types/userManagement";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import {ScopeSplitSelector, ScopeSelection} from "./components/ScopeSplitSelector";
import {IconButton} from "./import/inputs";
import {useScopeQueryParams} from "./hooks/useScopeQueryParams";
import {useSiren} from "../../pages/siren/SirenContext";

type FormState = {
    clientID: string;
    departmentID: string;
    broadcastDuration: number;
    broadcastDurationDisplayFormat: IntervalFormat;
    cycleCount: number;
    cycleInterval: number;
    cycleIntervalDisplayFormat: IntervalFormat;
    deliveryMethodInterval: number;
    deliveryMethodIntervalDisplayFormat: IntervalFormat;
};

const emptyForm = (): FormState => ({
    clientID: "",
    departmentID: "",
    broadcastDuration: 3600,
    broadcastDurationDisplayFormat: "hour",
    cycleCount: 2,
    cycleInterval: 1800,
    cycleIntervalDisplayFormat: "minute",
    deliveryMethodInterval: 300,
    deliveryMethodIntervalDisplayFormat: "minute",
});

// --- Inline config panel ---

type BroadcastConfigPanelProps = {
    scope: ScopeSelection;
    onDeleted: () => void;
};

const BroadcastConfigPanel: React.FC<BroadcastConfigPanelProps> = ({scope, onDeleted}) => {
    const {save, saving, error: saveError} = useUpsertSirenDefaultBroadcastConfig();
    const {deleteConfig, deleting} = useDeleteSirenDefaultBroadcastConfig();
    const {hasFullAccess} = usePermissions();

    const [record, setRecord] = useState<SirenDefaultBroadcastConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    useEffect(() => {
        setLoading(true);
        setSaved(false);
        setRecord(null);
        const params: Record<string, string> = {};
        if (scope.client) params.clientID = scope.client.id;
        if (scope.department) params.departmentID = scope.department.id;
        platformAPI.get<SirenDefaultBroadcastConfig>("api/siren/default-broadcast-config/exact", {params})
            .then(res => {
                const rec = res.data;
                setRecord(rec);
                setForm({
                    clientID: rec.clientID ?? "",
                    departmentID: rec.departmentID ?? "",
                    broadcastDuration: rec.broadcastDuration,
                    broadcastDurationDisplayFormat: (rec.broadcastDurationDisplayFormat as IntervalFormat) || "minute",
                    cycleCount: rec.cycleCount,
                    cycleInterval: rec.cycleInterval,
                    cycleIntervalDisplayFormat: (rec.cycleIntervalDisplayFormat as IntervalFormat) || "minute",
                    deliveryMethodInterval: rec.deliveryMethodInterval,
                    deliveryMethodIntervalDisplayFormat: (rec.deliveryMethodIntervalDisplayFormat as IntervalFormat) || "minute",
                });
            })
            .catch(() => {
                setForm({...emptyForm(), clientID: scope.client?.id ?? "", departmentID: scope.department?.id ?? ""});
            })
            .finally(() => setLoading(false));
    }, [scope.client?.id, scope.department?.id]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                broadcastDuration: form.broadcastDuration,
                broadcastDurationDisplayFormat: form.broadcastDurationDisplayFormat,
                cycleCount: form.cycleCount,
                cycleInterval: form.cycleInterval,
                cycleIntervalDisplayFormat: form.cycleIntervalDisplayFormat,
                deliveryMethodInterval: form.deliveryMethodInterval,
                deliveryMethodIntervalDisplayFormat: form.deliveryMethodIntervalDisplayFormat,
            });
            setRecord(result);
            setSaved(true);
        } catch {
            // error shown via saveError
        }
    }, [scope, form, save]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteConfig(record.id, {clientID: scope.client?.id, departmentID: scope.department?.id});
            setRecord(null);
            setForm({...emptyForm(), clientID: scope.client?.id ?? "", departmentID: scope.department?.id ?? ""});
            onDeleted();
        } catch {
            // silently ignore
        }
    }, [record, deleteConfig, scope, onDeleted]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-4 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div>
                <p className="font-semibold text-sm">{scope.client ? scope.client.name : "System Default"}</p>
                <p className="text-xs text-light-grey">{scope.client ? (scope.department ? scope.department.name : "Client level") : "Global fallback — no client"}</p>
            </div>

            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3">
                <IntervalInputV2
                    label="Broadcast Duration"
                    initialValue={form.broadcastDuration}
                    initialFormat={form.broadcastDurationDisplayFormat}
                    onChange={(value, format) => setForm(f => ({...f, broadcastDuration: value, broadcastDurationDisplayFormat: format}))}
                />
                <div className="flex flex-col gap-1">
                    <label className="font-medium text-sm">Cycle Count</label>
                    <input
                        type="number"
                        min={0}
                        className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                        value={form.cycleCount}
                        onChange={e => setForm(f => ({...f, cycleCount: parseInt(e.target.value, 10) || 0}))}
                    />
                </div>
                <IntervalInputV2
                    label="Cycle Interval"
                    initialValue={form.cycleInterval}
                    initialFormat={form.cycleIntervalDisplayFormat}
                    onChange={(value, format) => setForm(f => ({...f, cycleInterval: value, cycleIntervalDisplayFormat: format}))}
                />
                <IntervalInputV2
                    label="Delivery Method Interval"
                    initialValue={form.deliveryMethodInterval}
                    initialFormat={form.deliveryMethodIntervalDisplayFormat}
                    onChange={(value, format) => setForm(f => ({...f, deliveryMethodInterval: value, deliveryMethodIntervalDisplayFormat: format}))}
                />
            </div>

            {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
            {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>}
            {hasFullAccess("siren_config") && (
                <div className="flex justify-end gap-2 items-center pb-2">
                    {record && (
                        <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-32" onClick={() => setDeleteConfirm(true)}/>
                    )}
                    <IconButton text={record ? "Update" : "Create"} icon={record ? <SaveIcon style={{fontSize: 16}}/> : <AddIcon style={{fontSize: 16}}/>} className="w-32" onClick={handleSave} loading={saving} disabled={saving}/>
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Broadcast Config</h3>
                        <p className="text-sm">Delete the broadcast config for <span className="font-semibold">{scope.client ? scope.client.name : "System Default"}</span>{scope.department ? <> / <span className="font-semibold">{scope.department.name}</span></> : ""}?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- List page ---

const SirenDefaultBroadcastConfigPage: React.FC = () => {
    const {clientID} = useSiren();
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const {scope: selectedScope, drillClientID, drillClientName, deptPage, onSelect, onDrill, onDeptPageChange, onClear} = useScopeQueryParams({defaultClientID: isSuperAdmin ? null : clientID});

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ScopeSplitSelector
                        selectedScope={selectedScope}
                        onSelect={onSelect}
                        onClear={onClear}
                        onDrill={onDrill}
                        initialClientID={drillClientID}
                        initialClientName={drillClientName}
                        deptPage={deptPage}
                        onDeptPageChange={onDeptPageChange}
                        hydrate="broadcastConfigExistence"
                        showSystemDefault={isSuperAdmin}
                        showClientLevel={!isDepartmentAdmin}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {selectedScope ? (
                        <BroadcastConfigPanel
                            key={`${selectedScope.client?.id ?? "system"}-${selectedScope.department?.id ?? "client"}`}
                            scope={selectedScope}
                            onDeleted={() => {}}
                        />
                    ) : (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client, then a scope to configure.
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

// --- Edit / Create page ---

export const SirenDefaultBroadcastConfigEditPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {save, saving, error: saveError} = useUpsertSirenDefaultBroadcastConfig();
    const {isSuperAdmin} = useAuth();

    const [record, setRecord] = useState<SirenDefaultBroadcastConfig | null>(null);
    const [loading, setLoading] = useState(!!id);
    const [form, setForm] = useState<FormState>(emptyForm());
    const [saved, setSaved] = useState(false);

    const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | undefined>(undefined);

    React.useEffect(() => {
        if (!id) return;
        platformAPI.get<SirenDefaultBroadcastConfig>(`api/siren/default-broadcast-config/${id}`)
            .then(res => {
                setRecord(res.data);
                setForm({
                    clientID: res.data.clientID ?? "",
                    departmentID: res.data.departmentID ?? "",
                    broadcastDuration: res.data.broadcastDuration,
                    broadcastDurationDisplayFormat: (res.data.broadcastDurationDisplayFormat as IntervalFormat) || "minute",
                    cycleCount: res.data.cycleCount,
                    cycleInterval: res.data.cycleInterval,
                    cycleIntervalDisplayFormat: (res.data.cycleIntervalDisplayFormat as IntervalFormat) || "minute",
                    deliveryMethodInterval: res.data.deliveryMethodInterval,
                    deliveryMethodIntervalDisplayFormat: (res.data.deliveryMethodIntervalDisplayFormat as IntervalFormat) || "minute",
                });
            })
            .finally(() => setLoading(false));
    }, [id]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: form.clientID || undefined,
                departmentID: form.departmentID || undefined,
                broadcastDuration: form.broadcastDuration,
                broadcastDurationDisplayFormat: form.broadcastDurationDisplayFormat,
                cycleCount: form.cycleCount,
                cycleInterval: form.cycleInterval,
                cycleIntervalDisplayFormat: form.cycleIntervalDisplayFormat,
                deliveryMethodInterval: form.deliveryMethodInterval,
                deliveryMethodIntervalDisplayFormat: form.deliveryMethodIntervalDisplayFormat,
            });
            setRecord(result);
            setSaved(true);
            if (!id) navigate(`/siren/default-broadcast-config/edit/${result.id}`, {replace: true});
        } catch {
            // error shown via saveError
        }
    }, [form, save, id, navigate]);

    if (loading) return <Layout menuCurrent="config"><div className="p-4 text-sm text-light-grey">Loading…</div></Layout>;

    return (
        <Layout menuCurrent="config">
            <div className="flex flex-col gap-4">
                <div className="bg-white shadow-md rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">Scope</p>

                    {isSuperAdmin && (
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="font-medium text-sm">Client <span className="text-light-grey font-normal">(leave blank for system default)</span></label>
                                <ClientSelectSingle
                                    module="siren"
                                    client={selectedClient}
                                    initialClientID={form.clientID || undefined}
                                    onChange={c => {
                                        setSelectedClient(c);
                                        setSelectedDepartment(undefined);
                                        setForm(f => ({...f, clientID: c?.id ?? "", departmentID: ""}));
                                    }}
                                    disabled={!!record}
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="font-medium text-sm">Department <span className="text-light-grey font-normal">(optional)</span></label>
                                <DepartmentSelectSingle
                                    department={selectedDepartment}
                                    clientID={form.clientID || undefined}
                                    initialDepartmentID={form.departmentID || undefined}
                                    onChange={d => {
                                        setSelectedDepartment(d);
                                        setForm(f => ({...f, departmentID: d?.id ?? ""}));
                                    }}
                                    disabled={!!record || !form.clientID}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="bg-white shadow-md rounded-lg p-5 flex flex-col gap-4">
                    <p className="text-sm font-semibold text-light-grey uppercase tracking-wide">Broadcast Timing</p>

                    <IntervalInputV2
                        label="Broadcast Duration"
                        initialValue={form.broadcastDuration}
                        initialFormat={form.broadcastDurationDisplayFormat}
                        onChange={(value, format) => setForm(f => ({...f, broadcastDuration: value, broadcastDurationDisplayFormat: format}))}
                    />

                    <div className="flex flex-col gap-1">
                        <label className="font-medium text-sm">Cycle Count</label>
                        <input
                            type="number"
                            min={0}
                            className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                            value={form.cycleCount}
                            onChange={e => setForm(f => ({...f, cycleCount: parseInt(e.target.value, 10) || 0}))}
                        />
                    </div>

                    <IntervalInputV2
                        label="Cycle Interval"
                        initialValue={form.cycleInterval}
                        initialFormat={form.cycleIntervalDisplayFormat}
                        onChange={(value, format) => setForm(f => ({...f, cycleInterval: value, cycleIntervalDisplayFormat: format}))}
                    />

                    <IntervalInputV2
                        label="Delivery Method Interval"
                        initialValue={form.deliveryMethodInterval}
                        initialFormat={form.deliveryMethodIntervalDisplayFormat}
                        onChange={(value, format) => setForm(f => ({...f, deliveryMethodInterval: value, deliveryMethodIntervalDisplayFormat: format}))}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
                    {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Default broadcast config saved</div>}
                    <div className="flex justify-end">
                        <div className="w-48">
                            <BlueButton text="Save" onClick={handleSave} loading={saving} disabled={saving}/>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SirenDefaultBroadcastConfigPage;
